import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Checkbox,
    DatePicker,
    Input,
    Modal,
    Select,
    Upload,
    message,
    theme,
} from 'antd';
import dayjs from 'dayjs';
import {
    AlertCircle,
    ChevronDown,
    Info,
    Pencil,
    ScanBarcode,
    X,
} from 'lucide-react';
import { BarcodeScannerModal } from '@/components/BarcodeScanner/BarcodeScannerModal';
import { Product } from '@/store/Product/types';
import {
    handleInfiniteScroll,
    hasOnlyNumber,
} from '@/components/ModalAddPlanogramFromProduct/_components/FormPlanogram/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useGetInfinityCategory } from '@/store/category/useGetInfinityCategories';
import {
    getPropertyNamesFromProductStatus,
    ProductStatus,
} from '@/interfaces/Planogram';
import {
    useCreateProductFromPlanogram,
    PlanogramStatus,
} from '@/store/Product/useCreatePlanogramFromProduct';
import { useUpdateProductFields } from '@/store/Product/useUpdateProductFields';
import { useCreateShopProduct } from '@/store/Product/useCreateShopProduct';
import { fetchShopProductByBarCode } from '@/store/Product/useShopProductByBarCode';
import type { SearchProductGlobalByBarCodeResponse } from '@/store/Product/useSearchProductGlobalByBarCode';
import { useGetStoresPaginated } from '@/store/Dashboard/Stores/useGetStoresPaginated';
import { validateImage } from '@/helpers/utils';
import { QuickAdjustPlanogram } from '../../types';
import {
    buildProductOverrides,
    formatCents,
    getApiErrorMessage,
    getAvatarColor,
    getInitials,
    getSearchTerm,
    MIN_SEARCH_LENGTH,
} from '../../utils';
import {
    getQuickAdjustModalLayout,
    useIsFullScreenModal,
} from '../modalLayout';
import ProductAvatar from '../ProductAvatar';
import { NumberStepper, PriceInput } from '../Stepper';
import { useBarcodeLookup } from './useBarcodeLookup';
import { useProductNameSearch } from './useProductNameSearch';
import * as S from './styles';

const { Option } = Select;

type AddProductModalProps = {
    open: boolean;
    storeId: number | undefined;
    onClose: () => void;
    onCreated: () => void;
    onEditPlanogram: (planogram: QuickAdjustPlanogram) => void;
};

type Step = 'search' | 'inPlanogram' | 'local' | 'globalPrompt' | 'create';

type CreateBanner = 'imported' | 'notFound' | null;

type CreateDraft = {
    name: string;
    categoryId?: number;
    lastPriceInCents: number;
    imageFile?: File;
    imageUrl?: string;
    imagePreview?: string;
    banner: CreateBanner;
};

const SEARCH_STEP_EXPANDED_HEIGHT = 600;

const EMPTY_CREATE_DRAFT: CreateDraft = {
    name: '',
    lastPriceInCents: 0,
    banner: null,
};

const INITIAL_PLANOGRAM_FIELDS = {
    priceListInCents: 0,
    priceFinalInCents: 0,
    standardAmount: 1,
    alertLevel: 0,
    stockAmount: 0,
    status: 'unlocked' as PlanogramStatus,
    planogramCode: '',
    dueDate: null as string | null,
};

type PlanogramFieldsValue = typeof INITIAL_PLANOGRAM_FIELDS;

const getPlanogramFieldsError = (fields: PlanogramFieldsValue) => {
    if (!fields.planogramCode.trim()) return 'Informe o código de planograma.';
    if (fields.standardAmount < 1)
        return 'A quantidade padrão deve ser no mínimo 1.';
    if (fields.priceListInCents < 1)
        return 'O preço lista deve ser maior que zero.';
    if (fields.priceFinalInCents < 1)
        return 'O preço App deve ser maior que zero.';
    return undefined;
};

type PlanogramFieldProps = {
    fields: PlanogramFieldsValue;
    onChange: React.Dispatch<React.SetStateAction<PlanogramFieldsValue>>;
};

const DueDateField = ({ fields, onChange }: PlanogramFieldProps) => (
    <S.Section>
        <S.FieldLabel>Data de validade</S.FieldLabel>
        <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder="Selecionar data"
            value={fields.dueDate ? dayjs(fields.dueDate) : null}
            onChange={(value) =>
                onChange((prev) => ({
                    ...prev,
                    dueDate: value ? value.format('YYYY-MM-DD') : null,
                }))
            }
        />
    </S.Section>
);

const PlanogramStatusField = ({ fields, onChange }: PlanogramFieldProps) => {
    const { token } = theme.useToken();
    return (
        <div>
            <S.FieldLabel>Status</S.FieldLabel>
            <Select
                style={{ width: '100%' }}
                classNames={{ popup: { root: S.SELECT_POPUP_CLASS } }}
                suffixIcon={
                    <ChevronDown size={16} color={token.colorTextTertiary} />
                }
                value={fields.status}
                onChange={(value) =>
                    onChange((prev) => ({
                        ...prev,
                        status: value as PlanogramStatus,
                    }))
                }
            >
                {getPropertyNamesFromProductStatus().map((key) => (
                    <Option key={key} value={key}>
                        {ProductStatus[key as keyof typeof ProductStatus]}
                    </Option>
                ))}
            </Select>
        </div>
    );
};

type PlanogramCodeFieldProps = PlanogramFieldProps & { showError: boolean };

const PlanogramCodeField = ({
    fields,
    onChange,
    showError,
}: PlanogramCodeFieldProps) => {
    return (
        <div>
            <S.FieldLabel>Código de planograma</S.FieldLabel>
            <Input
                className="qa-mono"
                value={fields.planogramCode}
                placeholder="Cód. de planograma"
                status={
                    showError && !fields.planogramCode.trim() ? 'error' : ''
                }
                onChange={(e) =>
                    onChange((prev) => ({
                        ...prev,
                        planogramCode: e.target.value,
                    }))
                }
            />
        </div>
    );
};

type PlanogramFieldsSectionProps = PlanogramFieldProps & {
    showCodeError: boolean;
    showMetaFields?: boolean;
    otherStores: { id: number; name: string }[];
    replicateStoreIds: number[];
    onReplicateChange: React.Dispatch<React.SetStateAction<number[]>>;
};

const PlanogramFieldsSection = ({
    fields,
    onChange,
    showCodeError,
    showMetaFields = true,
    otherStores,
    replicateStoreIds,
    onReplicateChange,
}: PlanogramFieldsSectionProps) => (
    <>
        {showMetaFields && <DueDateField fields={fields} onChange={onChange} />}

        <S.Section>
            <S.SectionTitle>Informações de planograma</S.SectionTitle>
            <S.FieldsGrid>
                <div>
                    <S.FieldLabel>Preço lista</S.FieldLabel>
                    <PriceInput
                        value={fields.priceListInCents}
                        isError={showCodeError && fields.priceListInCents < 1}
                        onChange={(value) =>
                            onChange((prev) => ({
                                ...prev,
                                priceListInCents: value,
                            }))
                        }
                    />
                </div>
                <div>
                    <S.FieldLabel>Preço App</S.FieldLabel>
                    <PriceInput
                        value={fields.priceFinalInCents}
                        isError={showCodeError && fields.priceFinalInCents < 1}
                        onChange={(value) =>
                            onChange((prev) => ({
                                ...prev,
                                priceFinalInCents: value,
                            }))
                        }
                        accent
                    />
                </div>
                <div>
                    <S.FieldLabel>Qtd. padrão</S.FieldLabel>
                    <NumberStepper
                        value={fields.standardAmount}
                        min={1}
                        isError={showCodeError && fields.standardAmount < 1}
                        onChange={(value) =>
                            onChange((prev) => ({
                                ...prev,
                                standardAmount: value,
                            }))
                        }
                    />
                </div>
                <div>
                    <S.FieldLabel>Nível de alerta</S.FieldLabel>
                    <NumberStepper
                        value={fields.alertLevel}
                        onChange={(value) =>
                            onChange((prev) => ({
                                ...prev,
                                alertLevel: value,
                            }))
                        }
                    />
                </div>
                <div>
                    <S.FieldLabel>Qtd. em estoque</S.FieldLabel>
                    <NumberStepper
                        value={fields.stockAmount}
                        onChange={(value) =>
                            onChange((prev) => ({
                                ...prev,
                                stockAmount: value,
                            }))
                        }
                    />
                </div>
                {showMetaFields && (
                    <>
                        <PlanogramStatusField
                            fields={fields}
                            onChange={onChange}
                        />
                        <PlanogramCodeField
                            fields={fields}
                            onChange={onChange}
                            showError={showCodeError}
                        />
                    </>
                )}
            </S.FieldsGrid>
        </S.Section>

        {otherStores.length > 0 && (
            <S.Section>
                <S.SectionTitle>
                    Replicar planograma para outras lojas
                </S.SectionTitle>
                <S.ReplicateStoresList>
                    {otherStores.map((store) => (
                        <Checkbox
                            key={store.id}
                            checked={replicateStoreIds.includes(store.id)}
                            onChange={(e) =>
                                onReplicateChange((prev) =>
                                    e.target.checked
                                        ? [...prev, store.id]
                                        : prev.filter((id) => id !== store.id),
                                )
                            }
                        >
                            {store.name}
                        </Checkbox>
                    ))}
                </S.ReplicateStoresList>
            </S.Section>
        )}
    </>
);

export const AddProductModal = ({
    open,
    storeId,
    onClose,
    onCreated,
    onEditPlanogram,
}: AddProductModalProps) => {
    const { token } = theme.useToken();
    const [step, setStep] = useState<Step>('search');
    const [query, setQuery] = useState('');
    const [barcode, setBarcode] = useState('');
    const [isBarcodeEditable, setIsBarcodeEditable] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [foundPlanogram, setFoundPlanogram] =
        useState<QuickAdjustPlanogram>();
    const [globalProduct, setGlobalProduct] =
        useState<SearchProductGlobalByBarCodeResponse>();
    const [selectedProduct, setSelectedProduct] = useState<Product>();
    const [createDraft, setCreateDraft] =
        useState<CreateDraft>(EMPTY_CREATE_DRAFT);
    const [isCreatingAll, setIsCreatingAll] = useState(false);
    const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
    const searchIdRef = useRef(0);
    const [isEditingProduct, setIsEditingProduct] = useState(false);
    const [productNameDraft, setProductNameDraft] = useState('');
    const [productCategoryIdDraft, setProductCategoryIdDraft] =
        useState<number>();
    const [planogramFields, setPlanogramFields] = useState(
        INITIAL_PLANOGRAM_FIELDS,
    );
    const [replicateStoreIds, setReplicateStoreIds] = useState<number[]>([]);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const isFullScreen = useIsFullScreenModal();

    const { lookup, findPlanogramForProduct, isLookingUp } =
        useBarcodeLookup(storeId);

    const trimmedQuery = query.trim();
    const isBarcodeQuery = hasOnlyNumber(trimmedQuery);
    const debouncedQuery = useDebounce(query, 400);
    const nameTerm = isBarcodeQuery
        ? undefined
        : getSearchTerm(debouncedQuery)?.trim();
    const {
        products: nameResults,
        isLoading: isLoadingNameResults,
        isSuccess: isNameSearchSuccess,
        isError: isNameSearchError,
        isFetching: isFetchingNameResults,
        hasNextPage: hasNextPageNameResults,
        fetchNextPage: fetchNextPageNameResults,
    } = useProductNameSearch(open && step === 'search' ? nameTerm : undefined);
    const isNameSearchSettled =
        !!nameTerm && nameTerm === trimmedQuery && isNameSearchSuccess;
    const hasNoNameResults = isNameSearchSettled && nameResults.length === 0;

    const {
        data: categoriesPaginated,
        hasNextPage: hasNextPageCategories,
        isFetching: isFetchingCategories,
        fetchNextPage: fetchNextPageCategories,
    } = useGetInfinityCategory({});
    const categoryOptions = useMemo(
        () => categoriesPaginated?.pages.flatMap((page) => page.data) ?? [],
        [categoriesPaginated],
    );

    const {
        data: storesPaginated,
        hasNextPage: hasNextPageStores,
        isFetchingNextPage: isFetchingNextPageStores,
        fetchNextPage: fetchNextPageStores,
    } = useGetStoresPaginated(undefined, undefined, 50, open);
    useEffect(() => {
        if (hasNextPageStores && !isFetchingNextPageStores) {
            fetchNextPageStores();
        }
    }, [hasNextPageStores, isFetchingNextPageStores, fetchNextPageStores]);
    const stores = useMemo(
        () => storesPaginated?.pages.flatMap((page) => page.data) ?? [],
        [storesPaginated],
    );
    const otherStores = useMemo(
        () => stores.filter((store) => store.id !== storeId),
        [stores, storeId],
    );
    const currentStoreName =
        stores.find((store) => store.id === storeId)?.name ?? 'esta loja';

    const { mutateAsync: createPlanogram } = useCreateProductFromPlanogram();
    const { mutateAsync: updateProductFields } = useUpdateProductFields();
    const { mutateAsync: createShopProduct } = useCreateShopProduct();

    useEffect(
        () => () => {
            if (createDraft.imagePreview)
                URL.revokeObjectURL(createDraft.imagePreview);
        },
        [createDraft.imagePreview],
    );

    const resetState = () => {
        setStep('search');
        setQuery('');
        setBarcode('');
        setIsBarcodeEditable(false);
        setScannerOpen(false);
        setFoundPlanogram(undefined);
        setGlobalProduct(undefined);
        setSelectedProduct(undefined);
        setCreateDraft(EMPTY_CREATE_DRAFT);
        setIsCreatingAll(false);
        setIsSubmittingLocal(false);
        setIsEditingProduct(false);
        setProductNameDraft('');
        setProductCategoryIdDraft(undefined);
        setPlanogramFields(INITIAL_PLANOGRAM_FIELDS);
        setReplicateStoreIds([]);
        setSubmitAttempted(false);
    };

    const handleClose = () => {
        searchIdRef.current += 1;
        resetState();
        onClose();
    };

    const startLocalStep = (product: Product) => {
        setSelectedProduct(product);
        setProductNameDraft(product.name);
        setProductCategoryIdDraft(product.Category?.id);
        setStep('local');
    };

    const handleSearch = async (code: string) => {
        if (!code) return;

        searchIdRef.current += 1;
        const searchId = searchIdRef.current;
        setBarcode(code);
        setIsBarcodeEditable(false);

        try {
            const result = await lookup(code);
            if (searchId !== searchIdRef.current) return;

            if (result.kind === 'inPlanogram') {
                setFoundPlanogram(result.planogram);
                setStep('inPlanogram');
            } else if (result.kind === 'local') {
                startLocalStep(result.product);
            } else if (result.kind === 'global') {
                setGlobalProduct(result.global);
                setStep('globalPrompt');
            } else {
                setIsBarcodeEditable(!code);
                setCreateDraft({ ...EMPTY_CREATE_DRAFT, banner: 'notFound' });
                setStep('create');
            }
        } catch (error) {
            if (searchId !== searchIdRef.current) return;
            message.error(
                getApiErrorMessage(
                    error,
                    'Não foi possível buscar o produto. Tente novamente.',
                ),
            );
        }
    };

    const handleSelectProduct = async (product: Product) => {
        searchIdRef.current += 1;
        const searchId = searchIdRef.current;

        try {
            const planogram = await findPlanogramForProduct(product);
            if (searchId !== searchIdRef.current) return;

            setBarcode(product.BarCodes?.[0]?.code ?? '');
            setIsBarcodeEditable(false);
            if (planogram) {
                setFoundPlanogram(planogram);
                setStep('inPlanogram');
            } else {
                startLocalStep(product);
            }
        } catch (error) {
            if (searchId !== searchIdRef.current) return;
            message.error(
                getApiErrorMessage(
                    error,
                    'Não foi possível buscar o produto. Tente novamente.',
                ),
            );
        }
    };

    const openCreateFromName = () => {
        setBarcode('');
        setIsBarcodeEditable(true);
        setCreateDraft({
            ...EMPTY_CREATE_DRAFT,
            name: trimmedQuery,
            banner: 'notFound',
        });
        setStep('create');
    };

    const handleScanDetected = (code: string) => {
        const digits = code.replace(/\D/g, '');
        setScannerOpen(false);
        setQuery(digits);
        handleSearch(digits);
    };

    const handleSearchAgain = () => {
        setFoundPlanogram(undefined);
        setGlobalProduct(undefined);
        setSelectedProduct(undefined);
        setQuery('');
        setBarcode('');
        setIsBarcodeEditable(false);
        setStep('search');
    };

    const handleEditPlanogram = () => {
        if (!foundPlanogram) return;
        onEditPlanogram(foundPlanogram);
        handleClose();
    };

    const openBlankCreateForm = () => {
        setIsBarcodeEditable(!barcode);
        setCreateDraft(EMPTY_CREATE_DRAFT);
        setStep('create');
    };

    const openImportedCreateForm = () => {
        if (!globalProduct) return;

        setIsBarcodeEditable(!barcode);
        const globalCategoryName = globalProduct.Category?.name
            ?.trim()
            .toLowerCase();
        const matchedCategory = globalCategoryName
            ? categoryOptions.find(
                  (category) =>
                      category.name.trim().toLowerCase() === globalCategoryName,
              )
            : undefined;

        setCreateDraft({
            name: globalProduct.name,
            categoryId: matchedCategory
                ? Number(matchedCategory.id)
                : undefined,
            lastPriceInCents: Number(globalProduct.last_price_in_cents) || 0,
            imageUrl: globalProduct.image_url ?? undefined,
            imagePreview: undefined,
            banner: 'imported',
        });
        setStep('create');
    };

    const handleImageSelected = (file: File) => {
        const errors = validateImage(file);
        if (errors.length > 0) {
            message.error(errors.join('. '));
            return false;
        }
        setCreateDraft((prev) => ({
            ...prev,
            imageFile: file,
            imageUrl: undefined,
            imagePreview: URL.createObjectURL(file),
        }));
        return false;
    };

    const buildPlanogramPayload = (productId: number) => ({
        product_id: productId,
        storeIds: [String(storeId), ...replicateStoreIds.map(String)],
        planogram_code: planogramFields.planogramCode,
        status: planogramFields.status,
        price_list_in_cents: planogramFields.priceListInCents,
        price_final_in_cents: planogramFields.priceFinalInCents,
        standard_amount: planogramFields.standardAmount,
        alert_level: planogramFields.alertLevel,
        stock_amount: planogramFields.stockAmount,
        due_date: planogramFields.dueDate ?? undefined,
    });

    const handleSubmit = async () => {
        if (!selectedProduct || !storeId) return;

        setSubmitAttempted(true);
        const fieldsError = getPlanogramFieldsError(planogramFields);
        if (fieldsError) {
            message.error(fieldsError);
            return;
        }

        const overrides = isEditingProduct
            ? buildProductOverrides({
                  product: selectedProduct,
                  name: productNameDraft,
                  categoryId: productCategoryIdDraft,
              })
            : null;

        if (
            overrides &&
            productNameDraft !== selectedProduct.name &&
            !productNameDraft.trim()
        ) {
            message.error('O nome do produto não pode ficar vazio.');
            return;
        }

        setIsSubmittingLocal(true);
        try {
            if (overrides) {
                try {
                    await updateProductFields({
                        productId: selectedProduct.id,
                        overrides,
                    });
                } catch (error) {
                    message.error(
                        getApiErrorMessage(
                            error,
                            'Não foi possível atualizar os dados do produto. Tente novamente.',
                        ),
                    );
                    return;
                }
            }

            try {
                await createPlanogram(
                    buildPlanogramPayload(selectedProduct.id),
                );

                message.success('Produto adicionado ao planograma');
                onCreated();
                handleClose();
            } catch (error) {
                message.error(
                    getApiErrorMessage(
                        error,
                        overrides
                            ? 'Produto atualizado, mas não foi possível adicioná-lo ao planograma. Tente novamente.'
                            : 'Não foi possível adicionar o produto ao planograma. Tente novamente.',
                    ),
                );
            }
        } finally {
            setIsSubmittingLocal(false);
        }
    };

    const handleCreateSubmit = async () => {
        if (!storeId) return;

        if (!createDraft.name.trim()) {
            message.error('Informe o nome do produto.');
            return;
        }
        if (!createDraft.categoryId) {
            message.error('Selecione a categoria do produto.');
            return;
        }
        if (!barcode) {
            message.error('Informe o código de barras.');
            return;
        }

        setSubmitAttempted(true);
        const fieldsError = getPlanogramFieldsError(planogramFields);
        if (fieldsError) {
            message.error(fieldsError);
            return;
        }

        setIsCreatingAll(true);
        let productId: number | undefined;

        try {
            const created = await createShopProduct({
                name: createDraft.name.trim(),
                categoryId: createDraft.categoryId,
                barCode: barcode,
                lastPriceInCents: createDraft.lastPriceInCents,
                image: createDraft.imageFile,
                imageUrl: createDraft.imageFile
                    ? undefined
                    : createDraft.imageUrl,
            });
            productId =
                created?.id ?? (await fetchShopProductByBarCode(barcode))?.id;
        } catch (error) {
            message.error(
                getApiErrorMessage(
                    error,
                    'Não foi possível cadastrar o produto. Tente novamente.',
                ),
            );
            setIsCreatingAll(false);
            return;
        }

        if (!productId) {
            message.error(
                'Produto cadastrado, mas não foi possível adicioná-lo ao planograma',
            );
            setIsCreatingAll(false);
            handleSearchAgain();
            return;
        }

        try {
            await createPlanogram(buildPlanogramPayload(productId));

            message.success('Produto cadastrado e adicionado ao planograma');
            onCreated();
            handleClose();
        } catch (error) {
            message.error(
                getApiErrorMessage(
                    error,
                    'Produto cadastrado, mas não foi possível adicioná-lo ao planograma',
                ),
            );
            // Switch to the local step so a retry does not create a duplicate product
            const product = await fetchShopProductByBarCode(barcode).catch(
                () => undefined,
            );
            if (product) startLocalStep(product);
            else handleSearchAgain();
            setIsCreatingAll(false);
        }
    };

    const canSearch = isBarcodeQuery || hasNoNameResults;

    const handlePrimarySearch = () => {
        if (isLookingUp) return;
        if (isBarcodeQuery) handleSearch(trimmedQuery);
        else if (hasNoNameResults) openCreateFromName();
    };

    const renderFooter = () => {
        if (step === 'search') {
            return (
                <S.Footer>
                    <S.FooterSecondary $basis="38%">
                        <Button onClick={handleClose}>Cancelar</Button>
                    </S.FooterSecondary>
                    <S.FooterPrimary>
                        <Button
                            type="primary"
                            disabled={!canSearch}
                            loading={isLookingUp}
                            onClick={handlePrimarySearch}
                        >
                            {hasNoNameResults
                                ? 'Cadastrar produto'
                                : 'Buscar produto'}
                        </Button>
                    </S.FooterPrimary>
                </S.Footer>
            );
        }

        if (step === 'inPlanogram') {
            return (
                <S.Footer>
                    <S.FooterSecondary $basis="38%">
                        <Button onClick={handleClose}>Cancelar</Button>
                    </S.FooterSecondary>
                    <S.FooterLink>
                        <Button onClick={handleSearchAgain}>
                            Buscar outro
                        </Button>
                    </S.FooterLink>
                    <S.FooterPrimary>
                        <Button type="primary" onClick={handleEditPlanogram}>
                            Editar produto
                        </Button>
                    </S.FooterPrimary>
                </S.Footer>
            );
        }

        if (step === 'globalPrompt') {
            return (
                <S.Footer>
                    <S.FooterSecondary $basis="38%">
                        <Button onClick={handleClose}>Cancelar</Button>
                    </S.FooterSecondary>
                </S.Footer>
            );
        }

        const isCreateStep = step === 'create';
        return (
            <S.Footer>
                <S.FooterSecondary $basis="38%">
                    <Button onClick={handleClose}>Cancelar</Button>
                </S.FooterSecondary>
                <S.FooterPrimary>
                    <Button
                        type="primary"
                        loading={
                            isCreateStep ? isCreatingAll : isSubmittingLocal
                        }
                        onClick={
                            isCreateStep ? handleCreateSubmit : handleSubmit
                        }
                    >
                        {isCreateStep
                            ? 'Cadastrar e adicionar'
                            : 'Adicionar ao planograma'}
                    </Button>
                </S.FooterPrimary>
            </S.Footer>
        );
    };

    const renderNameResults = () => {
        if (isBarcodeQuery || trimmedQuery.length === 0) return null;

        if (nameTerm === trimmedQuery && isNameSearchError) {
            return (
                <S.ResultsMessage>
                    Não foi possível buscar os produtos. Tente novamente.
                </S.ResultsMessage>
            );
        }

        if (!nameTerm || nameTerm !== trimmedQuery || isLoadingNameResults) {
            return trimmedQuery.length >= MIN_SEARCH_LENGTH ? (
                <S.ResultsMessage>Buscando produtos...</S.ResultsMessage>
            ) : null;
        }

        if (hasNoNameResults) {
            return (
                <S.ResultsMessage>
                    Nenhum produto encontrado. Clique em &quot;Cadastrar
                    produto&quot; para criar um novo.
                </S.ResultsMessage>
            );
        }

        return (
            <S.ResultsList
                onScroll={(e) =>
                    handleInfiniteScroll({
                        e,
                        hasNextPage: hasNextPageNameResults,
                        isFetching: isFetchingNameResults,
                        fetchNextPage: fetchNextPageNameResults,
                    })
                }
            >
                {nameResults.map((product) => (
                    <S.ResultItem
                        key={product.id}
                        type="button"
                        disabled={isLookingUp}
                        onClick={() => handleSelectProduct(product)}
                    >
                        <ProductAvatar product={product} />
                        <S.ReferenceInfo>
                            <S.ReferenceName>{product.name}</S.ReferenceName>
                            <S.ReferenceMeta>
                                {product.BarCodes?.[0]?.code ||
                                    'Sem código de barras'}{' '}
                                · {product.Category?.name ?? 'Sem categoria'}
                            </S.ReferenceMeta>
                        </S.ReferenceInfo>
                    </S.ResultItem>
                ))}
            </S.ResultsList>
        );
    };

    const renderSearchStep = () => (
        <div>
            <S.FieldLabel htmlFor="quick-add-search">
                Nome ou código de barras
            </S.FieldLabel>
            <S.BarcodeInputGroup>
                <S.BarcodeInput
                    id="quick-add-search"
                    placeholder="Buscar por nome ou código de barras"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handlePrimarySearch();
                    }}
                    autoFocus
                />
                <S.ScanButton
                    type="button"
                    title="Ler código de barras com a câmera"
                    onClick={() => setScannerOpen(true)}
                >
                    <ScanBarcode size={18} />
                </S.ScanButton>
            </S.BarcodeInputGroup>
            {renderNameResults()}
        </div>
    );

    const renderInPlanogramStep = () => {
        if (!foundPlanogram) return null;
        const { product } = foundPlanogram;

        return (
            <>
                <S.Banner $tone="warning">
                    <AlertCircle size={16} />
                    <span>
                        <S.BannerTitle>
                            Este produto já está no planograma
                        </S.BannerTitle>
                        Ele já existe em {currentStoreName}. Você pode editar as
                        informações do planograma dele ou buscar outro produto.
                    </span>
                </S.Banner>
                <S.ReferenceCard>
                    <ProductAvatar product={product} size="large" />
                    <S.ReferenceInfo>
                        <S.ReferenceName>{product.name}</S.ReferenceName>
                        <S.ReferenceMeta>
                            {barcode || 'Sem código de barras'} ·{' '}
                            {product.Category?.name ?? 'Sem categoria'}
                        </S.ReferenceMeta>
                    </S.ReferenceInfo>
                </S.ReferenceCard>
                <S.InfoGrid>
                    <S.InfoCell>
                        <S.InfoCellLabel>Cód. planograma</S.InfoCellLabel>
                        <S.InfoCellValue>
                            {foundPlanogram.planogram_code || '—'}
                        </S.InfoCellValue>
                    </S.InfoCell>
                    <S.InfoCell>
                        <S.InfoCellLabel>Preço App</S.InfoCellLabel>
                        <S.InfoCellValue $accent>
                            {formatCents(foundPlanogram.price_final_in_cents)}
                        </S.InfoCellValue>
                    </S.InfoCell>
                    <S.InfoCell>
                        <S.InfoCellLabel>Qtd. padrão / estoque</S.InfoCellLabel>
                        <S.InfoCellValue>
                            {foundPlanogram.standard_amount} /{' '}
                            {foundPlanogram.stock_amount}
                        </S.InfoCellValue>
                    </S.InfoCell>
                    <S.InfoCell>
                        <S.InfoCellLabel>Nível de alerta</S.InfoCellLabel>
                        <S.InfoCellValue>
                            {foundPlanogram.alert_level}
                        </S.InfoCellValue>
                    </S.InfoCell>
                </S.InfoGrid>
            </>
        );
    };

    const renderLocalStep = () => {
        if (!selectedProduct) return null;

        return (
            <>
                <S.ReferenceCard>
                    <S.ReferenceAvatar
                        bg={getAvatarColor(selectedProduct.name)}
                    >
                        {getInitials(selectedProduct.name)}
                    </S.ReferenceAvatar>
                    <S.ReferenceInfo>
                        <S.ReferenceName>
                            {selectedProduct.name}
                        </S.ReferenceName>
                        <S.ReferenceMeta>
                            {selectedProduct.BarCodes?.[0]?.code ||
                                'Sem código de barras'}{' '}
                            ·{' '}
                            {selectedProduct.Category?.name ?? 'Sem categoria'}
                        </S.ReferenceMeta>
                    </S.ReferenceInfo>
                    <S.EditToggleButton
                        type="button"
                        onClick={() => setIsEditingProduct((prev) => !prev)}
                    >
                        <Pencil size={15} />
                    </S.EditToggleButton>
                </S.ReferenceCard>

                {isEditingProduct && (
                    <S.Section>
                        <S.SectionTitle>Produto</S.SectionTitle>
                        <S.FieldsGrid>
                            <div>
                                <S.FieldLabel>Nome do produto</S.FieldLabel>
                                <Input
                                    value={productNameDraft}
                                    onChange={(e) =>
                                        setProductNameDraft(e.target.value)
                                    }
                                />
                            </div>
                            <div>
                                <S.FieldLabel>Categoria</S.FieldLabel>
                                <Select
                                    style={{ width: '100%' }}
                                    classNames={{
                                        popup: { root: S.SELECT_POPUP_CLASS },
                                    }}
                                    suffixIcon={
                                        <ChevronDown
                                            size={16}
                                            color={token.colorTextTertiary}
                                        />
                                    }
                                    value={productCategoryIdDraft}
                                    onChange={(value) =>
                                        setProductCategoryIdDraft(
                                            value as number,
                                        )
                                    }
                                    onPopupScroll={(e) =>
                                        handleInfiniteScroll({
                                            e,
                                            hasNextPage: hasNextPageCategories,
                                            isFetching: isFetchingCategories,
                                            fetchNextPage:
                                                fetchNextPageCategories,
                                        })
                                    }
                                >
                                    {categoryOptions.map((category) => (
                                        <Option
                                            key={category.id}
                                            value={Number(category.id)}
                                        >
                                            {category.name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </S.FieldsGrid>
                    </S.Section>
                )}

                <PlanogramFieldsSection
                    fields={planogramFields}
                    onChange={setPlanogramFields}
                    showCodeError={submitAttempted}
                    otherStores={otherStores}
                    replicateStoreIds={replicateStoreIds}
                    onReplicateChange={setReplicateStoreIds}
                />
            </>
        );
    };

    const renderGlobalPromptStep = () => {
        if (!globalProduct) return null;

        return (
            <S.GlobalPanel>
                <S.GlobalPanelTitle>
                    Encontrado no repositório central
                </S.GlobalPanelTitle>
                <S.ReferenceCard>
                    <ProductAvatar
                        product={{
                            name: globalProduct.name,
                            image_url: globalProduct.image_url ?? '',
                        }}
                        size="large"
                    />
                    <S.ReferenceInfo>
                        <S.ReferenceName>{globalProduct.name}</S.ReferenceName>
                        <S.ReferenceMeta>
                            {globalProduct.Category?.name ?? 'Sem categoria'}
                        </S.ReferenceMeta>
                        <S.ReferenceMeta>{barcode}</S.ReferenceMeta>
                    </S.ReferenceInfo>
                </S.ReferenceCard>
                <S.GlobalNote>
                    Serão importados nome, categoria e imagem do produto. Você
                    poderá revisar tudo antes de cadastrar.
                </S.GlobalNote>
                <S.GlobalActions>
                    <Button block size="large" onClick={openBlankCreateForm}>
                        Preencher manualmente
                    </Button>
                    <Button
                        block
                        size="large"
                        type="primary"
                        onClick={openImportedCreateForm}
                    >
                        Usar dados do repositório
                    </Button>
                </S.GlobalActions>
            </S.GlobalPanel>
        );
    };

    const renderCreateStep = () => {
        const imageSrc = createDraft.imagePreview ?? createDraft.imageUrl;

        return (
            <>
                {createDraft.banner === 'imported' && (
                    <S.Banner $tone="info">
                        <Info size={16} />
                        <span>
                            Dados de produto importados do repositório central.
                        </span>
                    </S.Banner>
                )}
                {createDraft.banner === 'notFound' && (
                    <S.Banner $tone="warning">
                        <span>
                            Produto não encontrado. Cadastre-o abaixo junto com
                            as informações de planograma.
                        </span>
                    </S.Banner>
                )}

                <S.FieldsRow>
                    <div>
                        <S.FieldLabel>Imagem</S.FieldLabel>
                        <S.ImageDropzone>
                            <Upload.Dragger
                                accept=".png,.jpg,.jpeg"
                                showUploadList={false}
                                maxCount={1}
                                beforeUpload={handleImageSelected}
                            >
                                {imageSrc ? (
                                    <img
                                        src={imageSrc}
                                        alt={createDraft.name}
                                    />
                                ) : (
                                    <>
                                        <span>Adicionar</span>
                                        <u>ou procurar arquivos</u>
                                    </>
                                )}
                            </Upload.Dragger>
                        </S.ImageDropzone>
                    </div>
                    <S.FieldsRowGrow>
                        <S.FieldLabel>Nome do produto</S.FieldLabel>
                        <Input
                            value={createDraft.name}
                            onChange={(e) =>
                                setCreateDraft((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                        />
                    </S.FieldsRowGrow>
                </S.FieldsRow>

                <S.FieldGroup>
                    <S.FieldLabel>Categoria</S.FieldLabel>
                    <Select
                        style={{ width: '100%' }}
                        classNames={{ popup: { root: S.SELECT_POPUP_CLASS } }}
                        suffixIcon={
                            <ChevronDown
                                size={16}
                                color={token.colorTextTertiary}
                            />
                        }
                        placeholder="Selecione a categoria"
                        value={createDraft.categoryId}
                        onChange={(value) =>
                            setCreateDraft((prev) => ({
                                ...prev,
                                categoryId: value as number,
                            }))
                        }
                        onPopupScroll={(e) =>
                            handleInfiniteScroll({
                                e,
                                hasNextPage: hasNextPageCategories,
                                isFetching: isFetchingCategories,
                                fetchNextPage: fetchNextPageCategories,
                            })
                        }
                    >
                        {categoryOptions.map((category) => (
                            <Option
                                key={category.id}
                                value={Number(category.id)}
                            >
                                {category.name}
                            </Option>
                        ))}
                    </Select>
                </S.FieldGroup>

                <S.TwoColumnGrid>
                    <div>
                        <S.FieldLabel>Código de barras</S.FieldLabel>
                        {isBarcodeEditable ? (
                            <Input
                                className="qa-mono"
                                inputMode="numeric"
                                value={barcode}
                                onChange={(e) =>
                                    setBarcode(
                                        e.target.value.replace(/\D/g, ''),
                                    )
                                }
                            />
                        ) : (
                            <S.ReadOnlyValue>{barcode}</S.ReadOnlyValue>
                        )}
                    </div>
                    <div>
                        <S.FieldLabel>Último preço de compra un.</S.FieldLabel>
                        <PriceInput
                            value={createDraft.lastPriceInCents}
                            onChange={(value) =>
                                setCreateDraft((prev) => ({
                                    ...prev,
                                    lastPriceInCents: value,
                                }))
                            }
                        />
                    </div>
                    <PlanogramCodeField
                        fields={planogramFields}
                        onChange={setPlanogramFields}
                        showError={submitAttempted}
                    />
                    <PlanogramStatusField
                        fields={planogramFields}
                        onChange={setPlanogramFields}
                    />
                </S.TwoColumnGrid>

                <DueDateField
                    fields={planogramFields}
                    onChange={setPlanogramFields}
                />

                <PlanogramFieldsSection
                    fields={planogramFields}
                    onChange={setPlanogramFields}
                    showCodeError={submitAttempted}
                    otherStores={otherStores}
                    replicateStoreIds={replicateStoreIds}
                    onReplicateChange={setReplicateStoreIds}
                    showMetaFields={false}
                />
            </>
        );
    };

    const isCompactStep =
        step === 'search' || step === 'inPlanogram' || step === 'globalPrompt';
    const baseLayout = getQuickAdjustModalLayout(isFullScreen, token);
    const modalLayout = isFullScreen
        ? baseLayout
        : {
              ...baseLayout,
              width: isCompactStep ? 520 : 560,
              ...(step === 'search' ? { centered: false } : {}),
              style: {
                  ...baseLayout.style,
                  height: isCompactStep
                      ? 'auto'
                      : 'min(813px, calc(100vh - 48px))',
                  ...(step === 'search'
                      ? {
                            top: `max(24px, calc((100vh - ${SEARCH_STEP_EXPANDED_HEIGHT}px) / 2))`,
                        }
                      : {}),
              },
          };

    return (
        <>
            <S.SelectPopupGlobalStyle />
            <Modal
                title={
                    <S.HeaderRow>
                        <div>
                            <S.HeaderTitle>
                                Adicionar produto ao planograma
                            </S.HeaderTitle>
                            <S.HeaderSubtitle>
                                {step === 'search'
                                    ? 'Busque pelo nome ou código de barras para começar'
                                    : barcode
                                    ? `Código de barras ${barcode}`
                                    : step === 'create'
                                    ? 'Cadastrar novo produto'
                                    : 'Sem código de barras'}
                            </S.HeaderSubtitle>
                        </div>
                        <S.CloseButton
                            type="button"
                            aria-label="Fechar"
                            onClick={handleClose}
                        >
                            <X size={16} />
                        </S.CloseButton>
                    </S.HeaderRow>
                }
                open={open}
                closable={false}
                onCancel={handleClose}
                destroyOnHidden
                {...modalLayout}
                footer={renderFooter()}
            >
                <S.Body>
                    {step === 'search' && renderSearchStep()}
                    {step === 'inPlanogram' && renderInPlanogramStep()}
                    {step === 'local' && renderLocalStep()}
                    {step === 'globalPrompt' && renderGlobalPromptStep()}
                    {step === 'create' && renderCreateStep()}
                </S.Body>
            </Modal>

            <BarcodeScannerModal
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onDetected={handleScanDetected}
            />
        </>
    );
};

export default AddProductModal;
